import type { RefObject } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { VOLUME_STEP } from '../../constants';

import type { VideoUploadProps } from '../../VideoUpload.types';

export const useVolume = (
  videoRef: RefObject<HTMLVideoElement>,
  video: VideoUploadProps['video'],
) => {
  const [volume, _setVolume] = useState(1);
  const [isMuted, _setIsMuted] = useState(true);

  const setVolume = useCallback(
    (value: number) => {
      if (!videoRef.current) {
        return;
      }

      videoRef.current.volume = value;
    },
    [videoRef],
  );

  const increaseVolume = useCallback(() => {
    if (!videoRef.current) {
      return;
    }

    videoRef.current.volume = Math.min(
      videoRef.current.volume + VOLUME_STEP,
      1,
    );
  }, [videoRef]);

  const decreaseVolume = useCallback(() => {
    if (!videoRef.current) {
      return;
    }

    videoRef.current.volume = Math.max(
      videoRef.current.volume - VOLUME_STEP,
      0,
    );
  }, [videoRef]);

  const toggleMuted = useCallback(() => {
    if (!videoRef.current) {
      return;
    }

    videoRef.current.muted = !videoRef.current.muted;
  }, [videoRef]);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) {
      return;
    }

    _setVolume(videoElement.volume);
    _setIsMuted(videoElement.muted);

    videoElement.addEventListener('volumechange', () => {
      _setVolume(videoElement.volume);
      _setIsMuted(videoElement.muted);
    });

    return () => {
      videoElement.removeEventListener('volumechange', () => {
        _setVolume(videoElement.volume);
        _setIsMuted(videoElement.muted);
      });
    };
  }, [videoRef]);

  return {
    hasAudio: video?.hasAudio ?? false,
    volume,
    isMuted,
    setVolume,
    toggleMuted,
    increaseVolume,
    decreaseVolume,
  };
};
